import { type WidgetSettingsDefinition } from "@repo/api";
import { Button, ListViewItem, calendarIcon } from "@repo/skapa";
import { t, useTranslation } from "@repo/translation";
import {
  ModalSheet,
  SectionContainer,
  localizeDateAndTime,
  translateLabel,
} from "@repo/ui";
import { combineDates } from "@repo/utils";
import { useEffect, useState } from "react";

import { CustomRangePicker } from "./CustomRangePicker";
import { PresetRadioGroup } from "./PresetRadioGroup";
import { dateOptions } from "./utils";

const TWO_DAYS_IN_MS = 2 * 24 * 60 * 60 * 1000;

export const QuickDateSelectorSettingsControl = ({
  settingDefinition,
  setting,
  setSetting,
}: {
  settingDefinition: WidgetSettingsDefinition;
  setting: Record<string, any>;
  setSetting: (newSetting: Record<string, any>) => void;
}) => {
  const { dateFnsLocale } = useTranslation();
  const { id: settingId, title } = settingDefinition;

  const value = setting[settingId];

  const normalizedValue = value
    ? {
        ...value,
        start:
          value.start instanceof Date ? value.start : new Date(value.start),
        end: value.end instanceof Date ? value.end : new Date(value.end),
        startTime: value.startTime ? new Date(value.startTime) : undefined,
        endTime: value.endTime ? new Date(value.endTime) : undefined,
        id: value.id,
      }
    : null;

  const defaultOption = dateOptions.find(
    (opt) => opt.id === settingDefinition.additionalControl?.defaultOptionId
  )!;
  const [selectedOption, setSelectedOption] = useState(
    normalizedValue ?? defaultOption
  );
  const [modalIsOpen, setModalIsOpen] = useState(false);

  // Set default option and sync with external changes
  useEffect(() => {
    if (!value && defaultOption) {
      setSelectedOption(defaultOption);
      setSetting({
        ...setting,
        [settingId]: defaultOption,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const isCustomRange = selectedOption.id === "custom_range";
  const maxEndDate = selectedOption.start
    ? new Date(selectedOption.start.getTime() + TWO_DAYS_IN_MS)
    : undefined;

  const onSave = () => {
    let start = selectedOption.start;
    let end = selectedOption.end;

    if (
      isCustomRange &&
      start &&
      selectedOption.startTime &&
      end &&
      selectedOption.endTime
    ) {
      start = combineDates(start, selectedOption.startTime);
      end = combineDates(end, selectedOption.endTime);
    }

    setSetting({
      ...setting,
      [settingId]: {
        id: selectedOption.id,
        start,
        end,
      },
    });

    setModalIsOpen(false);
  };

  return (
    <>
      <ListViewItem
        control="navigational"
        title={translateLabel(title)}
        description={
          selectedOption.id === "custom_range" &&
          selectedOption.start &&
          selectedOption.end
            ? (() => {
                const startStr = localizeDateAndTime(
                  selectedOption.start,
                  dateFnsLocale
                );
                const endStr = localizeDateAndTime(
                  selectedOption.end,
                  dateFnsLocale
                );

                return startStr === endStr
                  ? startStr
                  : `${startStr} - ${endStr}`;
              })()
            : t(`label.${selectedOption.id}`)
        }
        onClick={() => setModalIsOpen(true)}
        leadingIcon={calendarIcon}
      />

      <ModalSheet
        title={translateLabel(title)}
        visible={modalIsOpen}
        onClose={() => setModalIsOpen(false)}
        onBack={() => setModalIsOpen(false)}
        footer={
          <Button type="primary" text={t("action.save")} onClick={onSave} />
        }
      >
        <SectionContainer>
          <PresetRadioGroup
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
          />

          {isCustomRange && (
            <CustomRangePicker
              selectedOption={selectedOption}
              setSelectedOption={setSelectedOption}
              maxEndDate={maxEndDate}
            />
          )}
        </SectionContainer>
      </ModalSheet>
    </>
  );
};
